import { useEffect } from "react";

import { useMapStore } from "stores/map";

import { useSelectedWellFromStore } from "components/data-analyzer/hooks";

import { useTypeLogStore } from "../context/TypeLogStoreProvider";
import { selectSelectWell, selectSelectedWell } from "../context/typeLogSelectors";
import {
  deriveTypeLogErrorMessage,
  useTypeLogDocumentQuery
} from "./useTypeLogDocumentQuery";
import { useTypeLogWellsQuery } from "./useTypeLogWellsQuery";

export function useTypeLogData() {
  // Global/base well selection (well-list driven) — synced into this widget's
  // scoped selection below.
  const selectedWellFromGlobalStore = useSelectedWellFromStore();

  // Scoped client-selection store (this widget instance only) — replaces the
  // old `useTypeLog()`/`dispatch` pair.
  const selectedWell = useTypeLogStore(selectSelectedWell);
  const selectWell = useTypeLogStore(selectSelectWell);

  // zustand — map store's TypeLog-scoped well list. This is a separate,
  // pre-existing global concern (unrelated to TypeLogContext) and is left
  // untouched by this migration.
  const setTypeLogWells = useMapStore((s) => s.setTypeLogWells);

  // React Query — server data. `document`/`wells` were previously fetched
  // imperatively into TypeLogContext; they now own their own cache lifecycle
  // per STATE_OWNERSHIP_CONTRACT.md §3.1/§3.2.
  const wellsQuery = useTypeLogWellsQuery();
  const documentQuery = useTypeLogDocumentQuery(selectedWell);

  // `typeLogErrorMessage` is no longer stored — it is re-derived from the
  // document query's error state (and the current selection) on every render
  // (Standing Rule 6 — derive, don't duplicate).
  const typeLogErrorMessage = deriveTypeLogErrorMessage(selectedWell, documentQuery);

  // Sync the globally selected well into this widget's scoped selection, but
  // only once it's known to exist among the fetched type-log wells. This
  // reproduces the `findIndex`-based gate the old reducer-driven effect used:
  // a `findIndex` result of exactly `-1` (the wells list has loaded AND the
  // well genuinely isn't in it) is the only case that blocks the sync; while
  // the list is still loading, `wellsQuery.data` is `undefined` and the gate
  // is a no-op, matching the prior behavior exactly.
  useEffect(() => {
    if (
      selectedWellFromGlobalStore?.Uwi !== selectedWell?.Uwi ||
      selectedWellFromGlobalStore?.FormattedUwi !== selectedWell?.FormattedUwi
    ) {
      const selectedWellUwi =
        selectedWellFromGlobalStore?.Uwi || selectedWellFromGlobalStore?.FormattedUwi;
      const selectedWellIndex = wellsQuery.data?.findIndex(
        (well) => well.id === selectedWellUwi || well.value === selectedWellUwi
      );
      if (selectedWellIndex !== -1) {
        selectWell(selectedWellFromGlobalStore);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWellFromGlobalStore]);

  // Keep the map store's TypeLog well list in sync with the fetched wells,
  // and default-select the first fetched well — but only when this widget
  // instance doesn't already have a selection. `useTypeLogWellsQuery()` uses
  // a SHARED, unscoped query key across every open TypeLog widget, so a
  // background refetch triggered by opening a second widget produces a new
  // `wellsQuery.data` reference that is pushed to every subscriber. Gating on
  // `!selectedWell` ensures that reference change never clobbers a selection
  // this widget instance has already made, while still auto-selecting
  // `wells[0]` the first time a widget mounts with no selection yet
  // (including a second widget mounting after the first already populated
  // the shared query cache — react-query returns the cached data
  // synchronously, and this widget's own scoped `selectedWell` starts
  // unset).
  useEffect(() => {
    setTypeLogWells(wellsQuery.data ?? []);
    if (!selectedWell && wellsQuery.data?.length > 0) {
      selectWell({
        Uwi: wellsQuery.data[0].id,
        FormattedUwi: wellsQuery.data[0].value
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wellsQuery.data]);

  return {
    wells: wellsQuery.data,
    document: documentQuery.data,
    typeLogErrorMessage
  };
}
